import React, { useCallback, useEffect, useRef, useState } from "react";
import Loading from "react-fullscreen-loading";

// region Libraries
import {
  Button, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Grid
} from "@material-ui/core";
import { FormHandles } from "@unform/core";
import { Form } from "@unform/web";
import { TextField } from "unform-material-ui";
import EditIcon from "@material-ui/icons/Edit";
// endregion Libraries
// region Languages
import useTranslation from "src/translations/useTranslation";
import {
  GlobalMessages, ToastMessages,
  VehiclesModuleMessages
} from "@shared/languages/interfaces";
// endregion Languages
// region Services
import api from "@services/api";
// endregion Services
// region Hooks
import { useToast } from "@hooks/useToast";
// endregion Hooks
// region Styles
import DialogConfirmAction from "@components/Dialog/ConfirmAction";
import { ContainerModalFormVehicleCANParameters } from "./styles";
import utils from "@utils/useful-functions";
import { VehicleCurrentInfo } from "@shared/entities/reg_vehicle_current_infos.entity";
// endregion Styles

interface FormVehicleCANParametersProps {
  open: boolean;
  vehicleId: string;
  vehicleCode: string;
  onClose: () => void;
}

interface VehicleConfigData {
  fuel_tank_size?: number | null;
  fuel_tank_size_estimated?: number | null;
  fuel_tank_size_estimated_at?: Date | null;
  max_temperature_limit?: number | null;
  harsh_acceleration_threshold?: number | null;
  harsh_braking_threshold?: number | null;
  harsh_cornering_threshold?: number | null;
  max_rpm_limit?: number | null;
}

const FormVehicleCANParameters: React.FC<FormVehicleCANParametersProps> = ({
  open,
  vehicleId,
  vehicleCode,
  onClose
}) => {
  // region States
  const [loading, setLoading] = useState(false);
  const [configData, setConfigData] = useState<VehicleConfigData>({});
  const [shouldOpenModal, setShouldOpenModal] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [modalType, setModalType] = useState<"details" | "update">("details");
  // endregion States
  // region Hooks
  const { addToast } = useToast();
  const { t } = useTranslation();
  // endregion Hooks
  // region Refs
  const formRef = useRef<FormHandles>(null);
  const lastVehicleIdFetch = useRef("");
  // endregion Refs
  // region Functions
  /**
   * Process value number input
   * @param value The number value to be processed
   * @returns The processed value
   */
  const processValueNumberInput = (value: number | undefined | null) => (value ? Number(value) : null);

  // endregion Functions
  // region Handlers
  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    setShouldOpenModal(false);
    setConfigData({});
    setModalType("details");
    lastVehicleIdFetch.current = "";
    onClose();
  }, [onClose]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (formData: VehicleConfigData) => {
    try {
      // Filter only filled fields
      const updateData: VehicleConfigData = {};

      updateData.fuel_tank_size = processValueNumberInput(formData.fuel_tank_size);
      updateData.max_temperature_limit = processValueNumberInput(formData.max_temperature_limit);
      updateData.harsh_acceleration_threshold = processValueNumberInput(formData.harsh_acceleration_threshold);
      updateData.harsh_braking_threshold = processValueNumberInput(formData.harsh_braking_threshold);
      updateData.harsh_cornering_threshold = processValueNumberInput(formData.harsh_cornering_threshold);
      updateData.max_rpm_limit = processValueNumberInput(formData.max_rpm_limit);

      setLoading(true);
      const { data } = await api.post(`vehicles/update-config/${vehicleId}`, updateData);

      if (data.status === "success") {
        addToast({
          type: "success",
          title: t(ToastMessages.success),
          description: t(VehiclesModuleMessages.vehicleCANParametersUpdateSuccess)
        });
        handleClose();
      } else {
        addToast({
          type: "info",
          title: t(ToastMessages.alert),
          description: data.message
        });
      }

    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, addToast, t, handleClose]);

  /**
   * Reset the learned tank-capacity estimate — the detector re-estimates from scratch. The
   * user-provided capacity is untouched (BCF-263 handoff, decisions 1a/2a).
   */
  const handleResetEstimate = useCallback(async () => {
    try {
      setConfirmResetOpen(false);
      setLoading(true);
      const { data } = await api.post(`vehicles/reset-fuel-tank-estimate/${vehicleId}`);

      if (data.status === "success") {
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });
        setConfigData((current) => ({ ...current, fuel_tank_size_estimated: null, fuel_tank_size_estimated_at: null }));
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, addToast, t]);
  // endregion Handlers
  // region Effects

  /**
   * Load vehicle current info when modal opens
   */
  useEffect(() => {
    const loadVehicleConfig = async () => {
      if (!open || !vehicleId || lastVehicleIdFetch.current === vehicleId) return;

      try {
        setLoading(true);
        const { data } = await api.get(`vehicles/current-info/${vehicleId}`);

        if (data.status === "success") {
          const currentInfo = data.result as VehicleCurrentInfo;

          setConfigData({
            fuel_tank_size: currentInfo.fuel_tank_size,
            fuel_tank_size_estimated: currentInfo.fuel_tank_size_estimated,
            fuel_tank_size_estimated_at: currentInfo.fuel_tank_size_estimated_at,
            max_temperature_limit: currentInfo.max_temperature_limit,
            harsh_acceleration_threshold: currentInfo.harsh_acceleration_threshold,
            harsh_braking_threshold: currentInfo.harsh_braking_threshold,
            harsh_cornering_threshold: currentInfo.harsh_cornering_threshold,
            max_rpm_limit: currentInfo.max_rpm_limit
          });

          setShouldOpenModal(true);
          lastVehicleIdFetch.current = vehicleId;
        } else {
          addToast({
            type: "error",
            title: t(VehiclesModuleMessages.vehicleCANParametersLoadError),
            description: data.message
          });
          handleClose();
        }
      } catch (error) {
        utils.handleStandardError(error, t, addToast);
        handleClose();
      } finally {
        setLoading(false);
      }
    };

    loadVehicleConfig().then();
  }, [open, vehicleId, addToast, t, handleClose]);
  // endregion Effects

  return (
    <ContainerModalFormVehicleCANParameters id="modalFormVehicleConfig">
      <Loading loading={loading} />
      {shouldOpenModal && (
        <Dialog
          open={open && shouldOpenModal}
          onClose={handleClose}
          scroll="paper"
          container={document.getElementById("modalFormVehicleConfig")}
          maxWidth="lg"
        >
          <DialogTitle className="mHeader">
            <div className="content">
              <div className="title">{t(VehiclesModuleMessages.vehicleCANParameters)}</div>
              <div className="subtitle">{vehicleCode}</div>
            </div>
            <div className="actions">
              {modalType === "details" && (
                <Button disableRipple onClick={() => setModalType("update")}><EditIcon /></Button>
              )}
            </div>
          </DialogTitle>
          <DialogContent dividers className="mContent">
            <Form className="form" ref={formRef} onSubmit={handleSubmit}>
              <DialogContentText tabIndex={-1} component="div">
                <Grid container spacing={1}>
                  <Grid item xs={12} md={12} lg={6}>
                    <TextField
                      className="fuel_tank_size"
                      label={t(VehiclesModuleMessages.fuelTankSize)}
                      margin="dense"
                      disabled={modalType === "details"}
                      type="number"
                      variant="outlined"
                      name="fuel_tank_size"
                      InputLabelProps={{
                        shrink: formRef.current?.getFieldValue("fuel_tank_size")?.length > 0 ? true : undefined
                      }}
                      defaultValue={configData.fuel_tank_size ?? ""}
                    />
                    <div className="estimated-tank" title={t(VehiclesModuleMessages.fuelTankSizeEstimatedTooltip)}>
                      <span>
                        {t(VehiclesModuleMessages.fuelTankSizeEstimated)}:&nbsp;
                        <b>{configData.fuel_tank_size_estimated != null ? `${configData.fuel_tank_size_estimated} L` : "—"}</b>
                        {configData.fuel_tank_size_estimated_at
                          ? ` (${t(VehiclesModuleMessages.fuelTankSizeEstimatedAt)} `
                            + `${utils.formatDateIfHave(configData.fuel_tank_size_estimated_at, "date")})`
                          : ""}
                      </span>
                      {modalType === "update" && configData.fuel_tank_size_estimated != null && (
                        <Button disableRipple size="small" color="primary" onClick={() => setConfirmResetOpen(true)}>
                          {t(VehiclesModuleMessages.resetFuelTankEstimate)}
                        </Button>
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <TextField
                      className="max_temperature_limit"
                      label={t(VehiclesModuleMessages.maxTemperatureLimit)}
                      margin="dense"
                      disabled={modalType === "details"}
                      type="number"
                      variant="outlined"
                      name="max_temperature_limit"
                      InputLabelProps={{
                        shrink: formRef.current?.getFieldValue("max_temperature_limit")?.length > 0
                          ? true : undefined
                      }}
                      defaultValue={configData.max_temperature_limit ?? 105}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <TextField
                      className="harsh_acceleration_threshold"
                      label={t(VehiclesModuleMessages.harshAccelerationThreshold)}
                      margin="dense"
                      disabled={modalType === "details"}
                      type="number"
                      variant="outlined"
                      name="harsh_acceleration_threshold"
                      InputLabelProps={{
                        shrink: formRef.current?.getFieldValue("harsh_acceleration_threshold")?.length > 0
                          ? true : undefined
                      }}
                      defaultValue={configData.harsh_acceleration_threshold ?? ""}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <TextField
                      className="harsh_braking_threshold"
                      label={t(VehiclesModuleMessages.harshBrakingThreshold)}
                      margin="dense"
                      disabled={modalType === "details"}
                      type="number"
                      variant="outlined"
                      name="harsh_braking_threshold"
                      InputLabelProps={{
                        shrink: formRef.current?.getFieldValue("harsh_braking_threshold")?.length > 0
                          ? true : undefined
                      }}
                      defaultValue={configData.harsh_braking_threshold ?? ""}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <TextField
                      className="harsh_cornering_threshold"
                      label={t(VehiclesModuleMessages.harshCorneringThreshold)}
                      margin="dense"
                      disabled={modalType === "details"}
                      type="number"
                      variant="outlined"
                      name="harsh_cornering_threshold"
                      InputLabelProps={{
                        shrink: formRef.current?.getFieldValue("harsh_cornering_threshold")?.length > 0
                          ? true : undefined
                      }}
                      defaultValue={configData.harsh_cornering_threshold ?? ""}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <TextField
                      className="max_rpm_limit"
                      label={t(VehiclesModuleMessages.maxRpmLimit)}
                      margin="dense"
                      disabled={modalType === "details"}
                      type="number"
                      variant="outlined"
                      name="max_rpm_limit"
                      InputLabelProps={{
                        shrink: formRef.current?.getFieldValue("max_rpm_limit")?.length > 0 ? true : undefined
                      }}
                      defaultValue={configData.max_rpm_limit ?? ""}
                    />
                  </Grid>
                </Grid>
              </DialogContentText>
            </Form>
          </DialogContent>
          <DialogActions className="mFooter">
            <div className="actions">
              <Button
                disableRipple
                type="submit"
                color="primary"
                onClick={() => formRef.current?.submitForm()}
              >
                {t(GlobalMessages.save)}
              </Button>
            </div>
            <Button disableRipple onClick={handleClose} color="primary">
              {t(GlobalMessages.close)}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <DialogConfirmAction
        open={confirmResetOpen}
        onClose={() => setConfirmResetOpen(false)}
        title={t(VehiclesModuleMessages.resetFuelTankEstimate)}
        actions={[
          { text: t(GlobalMessages.no), action: () => setConfirmResetOpen(false) },
          { text: t(GlobalMessages.yes), action: handleResetEstimate }
        ]}
      >
        {t(VehiclesModuleMessages.resetFuelTankEstimateConfirm)}
      </DialogConfirmAction>
    </ContainerModalFormVehicleCANParameters>
  );
};

export default FormVehicleCANParameters;
